/**
 * ModelFailureModal — opens when a chat run fails because no model could answer (bad or empty keys, no credits,
 * retired models). Explains it in plain language, lists what happened per model, and leads to the Models page.
 */
import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { AlertTriangle, ArrowRight, ChevronDown, X } from "lucide-react";
import { redactSecrets } from "../../utils/modelFailure";

const STEPS = [
  "Open Models and add a model you have access to (OpenAI, Anthropic, Google Gemini, Groq…)",
  "Paste its API key, or replace a key that is out of credits",
  "Pick that model in the chat and send your message again",
];

export default function ModelFailureModal({ failure, onClose, onGoToModels }) {
  const [showDetails, setShowDetails] = useState(false);
  useEffect(() => {
    const esc = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", esc);
    return () => document.removeEventListener("keydown", esc);
  }, [onClose]);

  return createPortal(
    <div
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
      style={{ position: "fixed", inset: 0, zIndex: 400, display: "flex", alignItems: "center", justifyContent: "center", padding: 16, background: "rgba(8,12,24,0.55)" }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Models could not answer"
        style={{ width: "min(520px, 100%)", maxHeight: "90vh", overflowY: "auto", padding: 24, borderRadius: 18, border: "1px solid var(--app-border-strong)", background: "var(--lyra-color-surface)", color: "var(--lyra-color-text)", boxShadow: "0 24px 60px rgba(8,12,24,0.4)", fontFamily: "inherit" }}
      >
        <div style={{ display: "flex", gap: 14, alignItems: "flex-start" }}>
          <div style={{ flexShrink: 0, width: 44, height: 44, borderRadius: 13, display: "grid", placeItems: "center", background: "color-mix(in srgb, var(--lyra-color-warning) 18%, transparent)", color: "var(--lyra-color-warning)" }}>
            <AlertTriangle size={22} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 17, fontWeight: 700, letterSpacing: "-0.01em" }}>Your models couldn't answer</div>
            <p style={{ margin: "4px 0 0", fontSize: 13.5, lineHeight: 1.5, color: "var(--lyra-color-text-muted)" }}>
              {failure.tried > 0 ? `Lyra tried ${failure.tried} model${failure.tried === 1 ? "" : "s"} and none responded.` : "No model responded."} This is almost always an API key or credit issue, not a problem with your question.
            </p>
          </div>
          <button type="button" onClick={onClose} aria-label="Close" style={{ display: "flex", padding: 4, border: "none", background: "none", cursor: "pointer", color: "var(--lyra-color-text-muted)" }}><X size={18} /></button>
        </div>

        {failure.groups.length > 0 && (
          <div style={{ marginTop: 18 }}>
            <div style={{ fontSize: 12, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--lyra-color-text-muted)", marginBottom: 8 }}>What happened</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {failure.groups.map((g) => (
                <div key={g.id} style={{ padding: "10px 12px", borderRadius: 11, border: "1px solid var(--lyra-color-border)", background: "var(--lyra-color-surface-2)" }}>
                  <div style={{ fontSize: 13, fontWeight: 600 }}>{g.label}</div>
                  <div style={{ marginTop: 6, display: "flex", flexWrap: "wrap", gap: 6 }}>
                    {g.models.map((m) => (
                      <span key={m} style={{ padding: "2px 9px", borderRadius: 999, fontSize: 11.5, fontWeight: 600, background: "var(--app-surface-3)", color: "var(--app-text-2)" }}>{m}</span>
                    ))}
                  </div>
                </div>
              ))}
              {failure.skipped.length > 0 && (
                <div style={{ fontSize: 12, color: "var(--lyra-color-text-muted)" }}>
                  {failure.skipped.length} other model{failure.skipped.length === 1 ? " was" : "s were"} skipped because no API key is saved for {failure.skipped.length === 1 ? "it" : "them"}.
                </div>
              )}
            </div>
          </div>
        )}

        <div style={{ marginTop: 18 }}>
          <div style={{ fontSize: 12, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--lyra-color-text-muted)", marginBottom: 8 }}>How to fix it</div>
          <ol style={{ listStyle: "none", margin: 0, padding: 0, display: "flex", flexDirection: "column", gap: 9 }}>
            {STEPS.map((s, i) => (
              <li key={s} style={{ display: "flex", gap: 10, alignItems: "flex-start", fontSize: 13.5, lineHeight: 1.45 }}>
                <span style={{ flexShrink: 0, width: 22, height: 22, borderRadius: "50%", display: "grid", placeItems: "center", fontSize: 11.5, fontWeight: 700, background: "var(--app-primary-soft-2)", color: "var(--app-primary-deep)" }}>{i + 1}</span>
                {s}
              </li>
            ))}
          </ol>
        </div>

        <div style={{ display: "flex", gap: 10, marginTop: 22, flexWrap: "wrap" }}>
          <button
            type="button"
            onClick={onGoToModels}
            style={{ flex: "1 1 220px", display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8, height: 42, border: "none", borderRadius: 11, background: "var(--lyra-color-primary)", color: "var(--lyra-color-primary-fg, #fff)", fontSize: 14, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}
          >
            Add an API key in Models <ArrowRight size={16} />
          </button>
          <button
            type="button"
            onClick={onClose}
            style={{ height: 42, padding: "0 18px", border: "1px solid var(--lyra-color-border)", borderRadius: 11, background: "var(--lyra-color-surface)", color: "var(--lyra-color-text)", fontSize: 14, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}
          >
            Close
          </button>
        </div>

        <button
          type="button"
          onClick={() => setShowDetails((v) => !v)}
          aria-expanded={showDetails}
          style={{ marginTop: 16, display: "inline-flex", alignItems: "center", gap: 5, border: "none", background: "none", padding: 0, cursor: "pointer", fontSize: 12.5, color: "var(--lyra-color-text-muted)", fontFamily: "inherit" }}
        >
          <ChevronDown size={14} style={{ transform: showDetails ? "rotate(180deg)" : "none", transition: "transform .15s" }} /> Technical details
        </button>
        {showDetails && (
          <pre style={{ margin: "8px 0 0", maxHeight: 180, overflow: "auto", padding: 10, borderRadius: 10, border: "1px solid var(--lyra-color-border)", background: "var(--lyra-color-surface-2)", color: "var(--app-text-2)", fontSize: 11, lineHeight: 1.5, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{redactSecrets(failure.raw)}</pre>
        )}
      </div>
    </div>,
    document.body,
  );
}
